import React, { useState, useEffect } from 'react';
import { 
  X, 
  AlertTriangle, 
  PencilRuler, 
  Users, 
  Link2, 
  Calendar, 
  MessageSquare,
  Search,
  Check,
  ChevronDown,
  FileText
} from 'lucide-react';
import { api, ConcernType, ConcernPriority, MonthlyPlan, Task } from '../../lib/api';

interface RaiseConcernModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | string;
  monthlyPlans?: MonthlyPlan[];
  preSelectedTaskIds?: number[];
  currentMonthlyPlanId?: number;
  tasks?: Task[];
  onSuccess?: () => void;
}

const CONCERN_TYPES: { value: ConcernType; label: string; description: string; icon: React.ReactNode }[] = [
  { 
    value: 'drawing_missing', 
    label: 'Drawing Missing', 
    description: 'Required drawings are not available or incomplete',
    icon: <PencilRuler className="w-5 h-5" /> 
  },
  { 
    value: 'resource_constraint', 
    label: 'Resource Constraint', 
    description: 'Insufficient manpower, materials, or equipment',
    icon: <Users className="w-5 h-5" /> 
  },
  { 
    value: 'dependency_issue', 
    label: 'Dependency Issue', 
    description: 'Blocked by other tasks or external factors',
    icon: <Link2 className="w-5 h-5" /> 
  },
  { 
    value: 'schedule_risk', 
    label: 'Schedule Risk', 
    description: 'Risk of delays or timeline issues',
    icon: <Calendar className="w-5 h-5" /> 
  },
  { 
    value: 'other', 
    label: 'Other', 
    description: 'Other concerns not covered above',
    icon: <MessageSquare className="w-5 h-5" /> 
  }
];

const PRIORITIES: { value: ConcernPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300' }
];

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const RaiseConcernModal: React.FC<RaiseConcernModalProps> = ({
  isOpen,
  onClose,
  projectId,
  monthlyPlans = [],
  preSelectedTaskIds = [],
  currentMonthlyPlanId,
  tasks = [],
  onSuccess
}) => {
  const [step, setStep] = useState(1);
  const [concernType, setConcernType] = useState<ConcernType | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<ConcernPriority>('medium');
  const [monthlyPlanId, setMonthlyPlanId] = useState<number | null>(currentMonthlyPlanId || null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>(preSelectedTaskIds);
  const [taskSearch, setTaskSearch] = useState('');
  const [showTaskPicker, setShowTaskPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setConcernType(null);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setMonthlyPlanId(currentMonthlyPlanId || null);
      setSelectedTaskIds(preSelectedTaskIds);
      setTaskSearch('');
      setShowTaskPicker(false);
    }
  }, [isOpen, preSelectedTaskIds, currentMonthlyPlanId]);

  const filteredTasks = tasks.filter(task => {
    if (!taskSearch) return true;
    const searchLower = taskSearch.toLowerCase();
    return (
      task.activity_name?.toLowerCase().includes(searchLower) ||
      task.category?.toLowerCase().includes(searchLower) ||
      task.floor?.toLowerCase().includes(searchLower) ||
      task.unit_name?.toLowerCase().includes(searchLower)
    );
  });

  const toggleTaskSelection = (taskId: number) => {
    setSelectedTaskIds(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSubmit = async () => {
    if (!concernType || !title.trim()) {
      alert('Please select a concern type and enter a title');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.createConcern(projectId, {
        concern_type: concernType,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        monthly_plan_id: monthlyPlanId || undefined,
        task_ids: selectedTaskIds.length > 0 ? selectedTaskIds : undefined
      });
      
      onSuccess?.();
      onClose();
    } catch (err: any) {
      alert(err.message || 'Failed to create concern');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Raise a Concern</h2>
              <p className="text-sm text-gray-500">Step {step} of 2</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-amber-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {step === 1 ? (
            /* Step 1: Select Concern Type */
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">What type of concern is this?</h3>
              <div className="space-y-2">
                {CONCERN_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setConcernType(type.value);
                      // Auto-suggest title based on type
                      if (!title) {
                        const suggestions: Record<ConcernType, string> = {
                          drawing_missing: 'Drawing not available for ',
                          resource_constraint: 'Resource shortage for ',
                          dependency_issue: 'Blocked by dependency: ',
                          schedule_risk: 'Schedule delay risk for ',
                          other: ''
                        };
                        setTitle(suggestions[type.value]);
                      }
                    }}
                    className={`w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all ${
                      concernType === type.value 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${concernType === type.value ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                      {type.icon}
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium text-gray-900">{type.label}</div>
                      <div className="text-sm text-gray-500">{type.description}</div>
                    </div>
                    {concernType === type.value && (
                      <Check className="w-5 h-5 text-amber-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Step 2: Details */
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concern Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief description of the concern..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide more details about the concern..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.value}
                      onClick={() => setPriority(p.value)}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        priority === p.value 
                          ? `${p.color} border-current` 
                          : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Monthly Plan (optional)
                </label>
                <select
                  value={monthlyPlanId || ''}
                  onChange={(e) => setMonthlyPlanId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">No specific plan</option>
                  {monthlyPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {MONTHS[plan.month]} {plan.year} ({plan.status})
                    </option>
                  ))}
                </select>
              </div>

              {/* Linked Tasks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Linked Tasks ({selectedTaskIds.length} selected)
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowTaskPicker(!showTaskPicker)}
                    className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-600">
                      {selectedTaskIds.length > 0 
                        ? `${selectedTaskIds.length} task(s) linked` 
                        : 'Click to select tasks...'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showTaskPicker ? 'rotate-180' : ''}`} />
                  </button>

                  {showTaskPicker && (
                    <div className="border-t border-gray-200">
                      {/* Search */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={taskSearch}
                            onChange={(e) => setTaskSearch(e.target.value)}
                            placeholder="Search tasks..."
                            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                        </div>
                      </div>

                      {/* Task List */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredTasks.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-500">
                            {tasks.length === 0 ? 'No tasks available' : 'No matching tasks'}
                          </div>
                        ) : (
                          filteredTasks.slice(0, 50).map(task => (
                            <label
                              key={task.id}
                              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaskIds.includes(task.id)}
                                onChange={() => toggleTaskSelection(task.id)}
                                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-900 truncate">{task.activity_name}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {[task.category, task.floor, task.unit_name].filter(Boolean).join(' • ')}
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                task.progress >= 100 ? 'bg-green-100 text-green-700' :
                                task.progress > 0 ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {task.progress || 0}%
                              </span>
                            </label>
                          ))
                        )}
                        {filteredTasks.length > 50 && (
                          <div className="p-2 text-center text-xs text-gray-400">
                            Showing first 50 results. Use search to narrow down.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected Tasks Preview */}
                {selectedTaskIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedTaskIds.slice(0, 5).map(id => {
                      const task = tasks.find(t => t.id === id);
                      return task ? (
                        <span 
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded"
                        >
                          <FileText className="w-3 h-3" />
                          {task.activity_name?.slice(0, 20)}
                          {(task.activity_name?.length || 0) > 20 && '...'}
                          <button
                            onClick={() => toggleTaskSelection(id)}
                            className="hover:text-amber-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                    {selectedTaskIds.length > 5 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        +{selectedTaskIds.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!concernType}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim()}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    Raise Concern
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaiseConcernModal;

